import { useMemo } from 'react';

import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  MenuItem,
  MenuItemProps,
} from '@/components/poppers/MenuPopper/MenuItem';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import {
  FetchHouseholdKgv2Query,
  useFetchHouseholdKgv2LazyQuery,
} from '@/modules/knowledgeGraphV2Visualizer/graphql/KnowledgeGraphV2Visualizer.generated';
import { Kgv2StatementWhereInput } from '@/types/schema';

import { DocumentsTableRowData } from '../DocumentsTable.types';

interface CopyDocumentKnowledgeGraphV2ActionItemProps
  extends Partial<MenuItemProps> {
  row: DocumentsTableRowData;
  householdId: string;
  disabled?: boolean;
}

interface CopyableKgv2Node {
  id: string;
  nodeType: string;
  namePrimary: string;
  state: string;
}

interface CopyableKgv2StatementEvidence {
  id: string;
  documentId: string;
  page: number | null;
  citation: string | null;
  snippet: string | null;
  reasoning: string | null;
}

interface CopyableKgv2Statement {
  id: string;
  predicate: string;
  polarity: string;
  confidence: number | null;
  value: string | null;
  fact: string | null;
  subjectId: string;
  objectId: string | null;
  documentId: string | null;
  evidence: CopyableKgv2StatementEvidence[];
}

interface CopyableKgv2Graph {
  id: string;
  name: string;
  status: string;
  nodes: CopyableKgv2Node[];
  statements: CopyableKgv2Statement[];
}

function toCopyableKgv2Graph(
  graph: NonNullable<FetchHouseholdKgv2Query['fetchHouseholdKGV2']>
): CopyableKgv2Graph {
  const nodeMap = new Map<string, CopyableKgv2Node>();

  const statements =
    graph.statements.edges?.flatMap((edge) => {
      if (!edge?.node) {
        return [];
      }

      const statement = edge.node;

      nodeMap.set(statement.subject.id, {
        id: statement.subject.id,
        nodeType: statement.subject.nodeType,
        namePrimary: statement.subject.namePrimary,
        state: statement.subject.state,
      });

      if (statement.object) {
        nodeMap.set(statement.object.id, {
          id: statement.object.id,
          nodeType: statement.object.nodeType,
          namePrimary: statement.object.namePrimary,
          state: statement.object.state,
        });
      }

      const evidence =
        statement.evidence.edges?.flatMap((evidenceEdge) => {
          if (!evidenceEdge?.node) {
            return [];
          }

          return [
            {
              id: evidenceEdge.node.id,
              documentId: evidenceEdge.node.document.id,
              page: evidenceEdge.node.page ?? null,
              citation: evidenceEdge.node.citation ?? null,
              snippet: evidenceEdge.node.snippet ?? null,
              reasoning: evidenceEdge.node.reasoning ?? null,
            },
          ];
        }) ?? [];

      return [
        {
          id: statement.id,
          predicate: statement.predicate,
          polarity: statement.polarity,
          confidence: statement.confidence ?? null,
          value: statement.value ?? null,
          fact: statement.fact ?? null,
          subjectId: statement.subject.id,
          objectId: statement.object?.id ?? null,
          documentId: statement.document?.document.id ?? null,
          evidence,
        },
      ];
    }) ?? [];

  return {
    id: graph.id,
    name: graph.name,
    status: graph.status,
    nodes: Array.from(nodeMap.values()),
    statements,
  };
}

/**
 * @description Action item for copying document-scoped KG V2 data.
 */
export function CopyDocumentKnowledgeGraphV2ActionItem({
  row,
  householdId,
  ...menuItemProps
}: CopyDocumentKnowledgeGraphV2ActionItemProps) {
  const [copyToClipboard] = useCopyToClipboard();
  const { createErrorFeedback, showFeedback } = useFeedback();

  const statementsWhere = useMemo<Kgv2StatementWhereInput>(() => {
    const documentFilter = { hasDocumentWith: [{ id: row.id }] };

    return {
      or: [
        { hasDocumentWith: [documentFilter] },
        { hasEvidenceWith: [documentFilter] },
      ],
    };
  }, [row.id]);

  const [copyKnowledgeGraphV2, { loading }] = useFetchHouseholdKgv2LazyQuery({
    variables: {
      input: {
        householdID: householdId,
      },
      statementsWhere,
    },
    onError: createErrorFeedback('Failed to copy document knowledge graph V2'),
    onCompleted: (data) => {
      if (!data?.fetchHouseholdKGV2) {
        return showFeedback('No knowledge graph V2 found', {
          variant: 'info-low',
        });
      }

      return copyToClipboard(
        JSON.stringify(toCopyableKgv2Graph(data.fetchHouseholdKGV2), null, 2),
        () =>
          showFeedback('Knowledge graph V2 copied to clipboard', {
            variant: 'success',
          }),
        () => showFeedback('Failed to copy to clipboard')
      );
    },
  });

  return (
    <MenuItem
      icon={<Copy07Icon size={18} />}
      loading={loading}
      label="Copy Knowledge Graph V2"
      onClick={() => copyKnowledgeGraphV2()}
      {...menuItemProps}
    />
  );
}
